/**
 * CapacityTab: the Team page's availability board — one table for every
 * billable staff member, care managers and SPICE therapists together,
 * grouped by unit, with its week picker.
 *
 * Rendered by the Team page for canViewWorkspace holders (the board floor:
 * office and managers). manage_staff_availability holders set each
 * person's stance on new clients (Open, Talk to Me, Not Open) inline and
 * open "More" for the rarer per-week fields (on leave, a note), for a care
 * manager and a SPICE therapist alike; the server re-checks it. A stance
 * save covers this week and next together, unless next week was already
 * set to a different stance (putNewClientStance), and the row stays
 * saving until the board has read the stored result back. Targets are
 * set on the People page. The map has its own tab; this one is the board
 * alone.
 *
 * The toggle, the chip and Notes show the stored override the server
 * sent on each row. The server sends it only to a seat that sees the
 * new-client stance, and a row without it shows no stance and offers no
 * write.
 *
 * The weekly call (LP-9): the week's mentions (who was named on each
 * care manager's call) ride the rows; the band above them is the
 * company's clients on hospice and recently died, from the record; the
 * dialog's Clients mentioned searches that care manager's caseload, and
 * greys On hospice and Passed away for a seat that does not set a
 * client's status (manage_client_identity).
 *
 * Week picker defaults to the current week in the operating zone and keeps
 * its choice in ?week=; "Last week" and "This week so far" move together
 * with it (last week = the week before the one selected). Hours count
 * when a note posts, imported CareTree time included, until internal
 * time ships; the panel says so.
 */

import { useMemo, useState, type ReactNode } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useTenant } from '@/lib/auth';
import { canManageClientIdentity, canManageStaffAvailability, canOpenEveryClientRecord } from '@/lib/capabilities';
import { Button } from '@/components/primitives/Button';
import { ErrorBanner } from '@/components/ErrorBanner';
import { SkeletonRow } from '@/components/Skeleton';
import { AvailabilityBoard, type PendingStance, type StanceError } from '@/components/capacity/AvailabilityBoard';
import { AvailabilityDialog } from '@/components/capacity/AvailabilityDialog';
import { addDays, currentWeekMonday, isoWeekMonday, weekLabel, weeklyTargetHours, type NewClientStance } from '@/lib/capacity';
import {
  toAvailabilityRows,
  useCallMentions,
  useCmCapacity,
  useHospiceAndDeaths,
  useSetNewClientStance,
  useSetStaffAvailability,
  type AvailabilityRowWire,
  type CallMentionWire,
} from '@/lib/capacityApi';
import { useCaseload } from '@/lib/workspace';
import { practiceTodayIso } from '@/lib/practiceDate';
import { ApiError } from '@/lib/api';
import {
  availabilityChangedText,
  contactSentence,
  errorCodeOf,
  sessionExpiredText,
  type SupportContact,
} from '@/lib/errorCopy';
import { availabilityRefusal, reportAppRefusal } from '@/lib/availabilityRefusal';
import { useSupportContact } from '@/hooks/useSupportContact';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function saveErrorMessage(err: unknown, contact: SupportContact): string {
  const expired = sessionExpiredText(err);
  if (expired !== null) return expired;
  // A hospice or passed-away mention from a seat that does not set a
  // client's status (the dialog greys both; this is the server's word).
  const code = errorCodeOf(err);
  if (code === 'mention_tag_not_allowed' || code === 'client_detail_not_allowed' || code === 'not_on_care_team') {
    return 'Supervisors and the office set On hospice and Passed away. Tag the client Other, or ask one of them.';
  }
  if (err instanceof ApiError && err.status === 403) {
    return contactSentence("Recording availability isn't part of your access. Ask {contact_admin} to record it.", contact);
  }
  return contactSentence("That availability didn't save. Try again, and if it happens again, tell {contact_system}.", contact);
}

export interface CapacityTabProps {
  tenantSlug: string;
}

export function CapacityTab({ tenantSlug }: CapacityTabProps): ReactNode {
  const { whoami } = useTenant();
  const canEdit = canManageStaffAvailability(whoami);
  const [searchParams, setSearchParams] = useSearchParams();
  const { pathname } = useLocation();

  const weekParam = searchParams.get('week');
  const week = weekParam && ISO_DATE_RE.test(weekParam) ? isoWeekMonday(weekParam) : currentWeekMonday();
  const thisWeek = currentWeekMonday();
  const setWeek = (next: string): void => {
    const p = new URLSearchParams(searchParams);
    if (next === thisWeek) p.delete('week');
    else p.set('week', next);
    setSearchParams(p, { replace: true });
  };

  const capacity = useCmCapacity(tenantSlug, week);
  const contact = useSupportContact();
  const rows = useMemo(
    () => toAvailabilityRows(capacity.careManagers, capacity.spiceTherapists),
    [capacity.careManagers, capacity.spiceTherapists],
  );

  // The weekly call: the week's mentions, the company band, and (while
  // the dialog is open) the caseload its search reads.
  const callMentions = useCallMentions(tenantSlug, week);
  const band = useHospiceAndDeaths(tenantSlug);
  const mentionsByRoster = useMemo(() => {
    const by = new Map<string, CallMentionWire[]>();
    for (const m of callMentions.mentions) by.set(m.roster_id, [...(by.get(m.roster_id) ?? []), m]);
    return by;
  }, [callMentions.mentions]);
  const canSetRecord = canManageClientIdentity(whoami);

  const save = useSetStaffAvailability(tenantSlug);
  const [editing, setEditing] = useState<AvailabilityRowWire | null>(null);
  const caseload = useCaseload(tenantSlug, { enabled: editing?.kind === 'care_manager' });
  // A new pick opens on what the record holds: the band carries every
  // client on hospice and every recent death, the week's mentions the rest.
  const recordDate = (clientId: string, tag: 'hospice' | 'passed_away'): string | null => {
    const list = tag === 'hospice' ? band.data?.on_hospice : band.data?.passed_away;
    const fromBand = list?.find((c) => c.client_id === clientId)?.date ?? null;
    if (fromBand !== null) return fromBand;
    const m = callMentions.mentions.find((x) => x.client_id === clientId);
    return (tag === 'hospice' ? m?.on_hospice_since : m?.date_of_death) ?? null;
  };
  const [saveError, setSaveError] = useState<string | null>(null);

  // The "New clients" toggle: its own capability is
  // manage_staff_availability, same as the "More" dialog: one seat, two
  // affordances on the same override row.
  const setNewClientStance = useSetNewClientStance(tenantSlug);
  const [pending, setPending] = useState<PendingStance | null>(null);
  const [stanceError, setStanceError] = useState<StanceError | null>(null);

  // The counts line names care managers and SPICE therapists apart, so a
  // viewer learns who's on the table without scanning every row, then
  // one hours-target count across both kinds (the target is hours for
  // everyone). Says nothing when there is truly nothing to count yet:
  // the board's own empty line speaks then.
  const summary = useMemo(() => {
    if (rows.length === 0) return '';
    const cmCount = rows.filter((r) => r.kind === 'care_manager').length;
    const spiceCount = rows.filter((r) => r.kind === 'spice_therapist').length;
    const withTarget = rows.filter((r) => weeklyTargetHours(r) !== null).length;
    const parts: string[] = [];
    if (cmCount > 0) parts.push(`${cmCount} care manager${cmCount === 1 ? '' : 's'}`);
    if (spiceCount > 0) parts.push(`${spiceCount} SPICE therapist${spiceCount === 1 ? '' : 's'}`);
    parts.push(`${withTarget} with target hours set`);
    return parts.join(' · ');
  }, [rows]);

  const handleSetNewClientStance = (row: AvailabilityRowWire, stance: NewClientStance): void => {
    setStanceError(null);
    setPending({ rosterId: row.roster_id, stance });
    setNewClientStance.mutate(
      { row, weekStart: week, stance },
      {
        // The hook's own onSuccess returns the board's refetch, so this
        // runs once the stored stance is back on the row.
        onSuccess: () => setPending(null),
        onError: (err) => {
          setPending(null);
          // A save over a newer change (0216): the board's own sentence,
          // and the board refetched, so the row shows what stands now (this
          // week may have saved while the following week was refused).
          const changed = availabilityChangedText(err, contact, 'board');
          if (changed !== null) void capacity.refetch();
          // The toggle types nothing, so any 400 is Olive's: said so, and
          // reported to the Errors tab.
          const refusal = availabilityRefusal(err);
          if (refusal?.appCaused) reportAppRefusal(tenantSlug, pathname, refusal.field);
          setStanceError({ rosterId: row.roster_id, message: changed ?? refusal?.sentence ?? saveErrorMessage(err, contact) });
        },
      },
    );
  };

  return (
    <div data-testid="capacity-tab">
      {summary !== '' && <p className="text-sm text-ink-soft mb-4" data-testid="capacity-summary">{summary}</p>}

      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="capacity-week-picker">
        <Button variant="secondary" size="sm" onClick={() => setWeek(addDays(week, -7))} data-testid="capacity-week-prev">
          Previous week
        </Button>
        <span className="text-sm font-semibold text-sage-800" data-testid="capacity-week-label">
          {weekLabel(week)}{week === thisWeek ? ' (this week)' : ''}
        </span>
        <Button variant="secondary" size="sm" onClick={() => setWeek(addDays(week, 7))} data-testid="capacity-week-next">
          Next week
        </Button>
        {week !== thisWeek && (
          <Button variant="ghost" size="sm" onClick={() => setWeek(thisWeek)} data-testid="capacity-week-today">
            This week
          </Button>
        )}
      </div>

      {capacity.isLoading && (
        <div><SkeletonRow /><SkeletonRow /><SkeletonRow /></div>
      )}
      {capacity.isError && <ErrorBanner error={capacity.error} onRetry={capacity.refetch} what="capacity" />}
      {!capacity.isLoading && !capacity.isError && (
        <AvailabilityBoard
          tenantSlug={tenantSlug}
          rows={rows}
          canEdit={canEdit}
          linkToCareManager={canOpenEveryClientRecord(whoami)}
          onMore={(row) => {
            setSaveError(null);
            setEditing(row);
          }}
          onSetNewClientStance={handleSetNewClientStance}
          pending={pending}
          stanceError={stanceError}
          mentionsByRoster={mentionsByRoster}
          hospiceAndDeaths={band.data}
          today={practiceTodayIso()}
        />
      )}

      {editing && (
        <AvailabilityDialog
          row={editing}
          weekStart={week}
          busy={save.isPending}
          error={saveError}
          // A care manager's call names clients, once the week's mentions are read.
          mentions={editing.kind === 'care_manager' && !callMentions.isLoading && !callMentions.isError ? (mentionsByRoster.get(editing.roster_id) ?? []) : undefined}
          caseload={caseload.careManagers.find((c) => c.roster_id === editing.roster_id)?.clients ?? []}
          canSetRecord={canSetRecord}
          recordDate={recordDate}
          onClose={() => setEditing(null)}
          onSave={(input) => {
            setSaveError(null);
            save.mutate(
              { rosterId: editing.roster_id, input },
              {
                onSuccess: () => setEditing(null),
                onError: (err) => {
                  const changed = availabilityChangedText(err, contact, 'dialog');
                  if (changed !== null) void capacity.refetch();
                  // A 400 names its field: the note over its cap is the
                  // person's to fix; anything else is Olive's, reported.
                  const refusal = availabilityRefusal(err, input.notes);
                  if (refusal?.appCaused) reportAppRefusal(tenantSlug, pathname, refusal.field);
                  setSaveError(changed ?? refusal?.sentence ?? saveErrorMessage(err, contact));
                },
              },
            );
          }}
        />
      )}
    </div>
  );
}
